import { candidatesStatus } from '@shared/helpers/candidate.helper';
import { IActivity } from '@shared/interfaces/activity.interface';
import { ITranslatable, TranslatableParam } from '@shared/interfaces/translatable.interface';
import {
  APIDatetime,
  APIuuid,
  IAPIArrayData,
  IAPIDataObject,
  IAPIRelationship
} from '@shared/interfaces/api.interface';
import { Candidate } from '@shared/models/candidate.model';
import { QuestionSet } from '@shared/models/question-set.model';
import { Recruiter } from '@shared/models/recruiter.model';
import { Reference } from '@shared/models/reference.model';
import { User } from '@shared/models/user.model';

// Roots of the feed keys in assets/i18n/*.json (`ACTIVITY_FEED`). A sentence
// takes translatable fragments as params, so the "actor known / actor unknown"
// wording ("Recruiter <b>X</b>" vs "A recruiter") lives in the locale files
// too; the `translatable` pipe resolves the nesting at render time.
const FEED = 'ACTIVITY_FEED';
const ACTORS = `${FEED}.ACTORS`;
const OBJECTS = `${FEED}.OBJECTS`;
const SENTENCES = `${FEED}.SENTENCES`;
const REQUEST_TYPES = `${FEED}.REQUEST_TYPES`;

type ActorKind = 'RECRUITER' | 'CANDIDATE' | 'NAME';

export class Activity implements IActivity {
  public static readonly TYPE: string = String('Activity');

  // Relationships
  trackable: any;
  owner: any;
  recipient?: any;

  // Attributes
  id: APIuuid;
  key: string;
  parameters: any;
  created_at: APIDatetime;

  // Memoized on first read: the getters below build fresh objects, and the
  // render sites (impure `translatable` pipe, `@for ... track l`) rely on a
  // stable identity to avoid re-translating and re-rendering on every
  // change-detection pass. Relationships are set right after construction in
  // fromAPIArray, before anything renders.
  private _description?: ITranslatable;
  private _oldValues?: ITranslatable[];
  private _newValues?: ITranslatable[];

  constructor(partial: Partial<IActivity>) {
    Object.assign(this, partial);
  }

  get oldValues(): ITranslatable[] {
    return (this._oldValues ??= !!this.parameters?.old
      ? this.getList(`${FEED}.VALUES.OLD`, this.parameters.old)
      : []);
  }

  get newValues(): ITranslatable[] {
    return (this._newValues ??= !!this.parameters?.new
      ? this.getList(`${FEED}.VALUES.NEW`, this.parameters.new)
      : []);
  }

  // The feed sentence as a translation key plus params; render it through the
  // `translatable` pipe. Names are wrapped in <b> by the fragments.
  get description(): ITranslatable {
    return (this._description ??= this.describe());
  }

  private describe(): ITranslatable {
    // Sentence-initial actors
    const recruiter = this.actor('RECRUITER', this.owner?.name_label);
    const candidateActor = this.actor('CANDIDATE', this.owner?.name_label);
    const ownerName = this.actor('NAME', this.owner?.name_label);
    const trackableName = this.actor('NAME', this.trackable?.name_label);
    const user = this.actor('NAME', this.trackable?.full_name);

    // Mid-sentence objects
    const recipientCandidate = this.object(
      'CANDIDATE',
      'A_CANDIDATE',
      this.recipient?.name_label
    );
    const recipientRecruiter = this.object(
      'RECRUITER',
      'A_RECRUITER',
      this.recipient?.name_label
    );
    const trackableCandidate = this.object(
      'CANDIDATE_LOWERCASE',
      'A_CANDIDATE',
      this.trackable?.name_label
    );
    const record = this.object('NAME', 'A_RECORD', this.recipient?.name_label);
    const document = this.object(
      'DOCUMENT',
      'A_DOCUMENT',
      this.parameters?.filename,
      'filename'
    );

    switch (this.key) {
      // References
      case 'recruiter_reference.create': {
        const reference = this.object(
          'AS_REFERENCE',
          'A_REFERENCE',
          this.trackable?.name_label
        );

        if (this.owner instanceof Candidate) {
          return this.sentence('RECRUITER_REFERENCE.CREATE_BY_CANDIDATE', {
            candidate: candidateActor,
            reference
          });
        }

        return this.sentence('RECRUITER_REFERENCE.CREATE_BY_RECRUITER', {
          recruiter,
          reference,
          candidate: recipientCandidate
        });
      }

      case 'recruiter_reference.complete_reference':
      case 'profile_reference.complete_reference':
        return this.sentence('RECRUITER_REFERENCE.COMPLETE_REFERENCE', {
          reference: trackableName,
          candidate: recipientCandidate
        });

      case 'recruiter_reference.update_reference':
        return this.sentence('RECRUITER_REFERENCE.UPDATE_REFERENCE', {
          recruiter,
          reference: this.object(
            'DETAILS_OF',
            'A_REFERENCE',
            this.trackable?.name_label
          )
        });

      case 'recruiter_reference.destroy':
        return this.sentence('RECRUITER_REFERENCE.DESTROY', { recruiter });

      case 'recruiter_reference.referencer_contact_method_update':
        return this.sentence(
          'RECRUITER_REFERENCE.REFERENCER_CONTACT_METHOD_UPDATE',
          { reference: trackableName }
        );

      case 'recruiter_request.complete_reference_request':
        return this.sentence('RECRUITER_REQUEST.COMPLETE_REFERENCE_REQUEST', {
          candidate: ownerName
        });

      // Candidate
      case 'candidate.create':
        return this.sentence('CANDIDATE.CREATE', {
          recruiter,
          candidate: this.object(
            'AS_CANDIDATE',
            'A_CANDIDATE',
            this.trackable?.name_label
          )
        });

      case 'candidate.update':
        return this.sentence('CANDIDATE.UPDATE', {
          recruiter,
          candidate: this.object('NAME', 'A_CANDIDATE', this.trackable?.name_label)
        });

      case 'candidate.update_status': {
        const params = {
          recruiter,
          candidate: this.object('NAME', 'A_CANDIDATE', this.trackable?.name_label)
        };
        const oldStatus = this.statusKey(this.parameters?.old);
        const newStatus = this.statusKey(this.parameters?.new);

        if (oldStatus === null || newStatus === null) {
          return this.sentence('CANDIDATE.UPDATE_STATUS', params);
        }

        return this.sentence('CANDIDATE.UPDATE_STATUS_FROM_TO', {
          ...params,
          oldStatus: { key: oldStatus },
          newStatus: { key: newStatus }
        });
      }

      case 'candidate.destroy':
        return this.sentence('CANDIDATE.DESTROY', {
          recruiter,
          candidate: trackableCandidate
        });

      case 'candidate.download_document':
        return this.sentence('CANDIDATE.DOWNLOAD_DOCUMENT', {
          recruiter,
          document,
          candidate: trackableCandidate
        });

      case 'candidate.upload_document':
        return this.candidateDocumentDescription('UPLOAD_DOCUMENT');

      case 'candidate.delete_document':
        return this.candidateDocumentDescription('DELETE_DOCUMENT');

      // Recruiter
      case 'recruiter.add_new':
      case 'recruiter.create':
        return this.sentence('RECRUITER.CREATE', {
          recruiter,
          newRecruiter: this.object(
            'AS_RECRUITER',
            'A_RECRUITER',
            this.trackable?.name_label
          )
        });

      case 'recruiter.set_inactive':
      case 'recruiter.set_active':
        return this.sentence(
          this.key === 'recruiter.set_inactive'
            ? 'RECRUITER.SET_INACTIVE'
            : 'RECRUITER.SET_ACTIVE',
          {
            recruiter,
            account: this.object(
              'RECRUITER_ACCOUNT',
              'A_RECRUITER_ACCOUNT',
              this.trackable?.name_label
            )
          }
        );

      // Reference Requests
      case 'recruiter_request.create':
        return this.sentence('RECRUITER_REQUEST.CREATE', {
          recruiter,
          candidate: recipientCandidate
        });

      // Question Sets
      case 'question_set.create':
        if (!!this.trackable?.title) {
          return this.sentence('QUESTION_SET.CREATE', {
            recruiter,
            title: this.trackable.title
          });
        }

        return this.sentence('QUESTION_SET.CREATE_UNTITLED', { recruiter });

      case 'question_set.destroy':
        return this.sentence('QUESTION_SET.DESTROY', { recruiter });

      case 'question_set.update':
        return this.sentence('QUESTION_SET.UPDATE', {
          recruiter,
          questionSet: this.object(
            'QUESTION_SET',
            'A_QUESTION_SET',
            this.trackable?.title,
            'title'
          )
        });

      case 'profile_reference.create':
        if (!!this.trackable?.name_label) {
          return this.sentence('PROFILE_REFERENCE.CREATE', {
            candidate: candidateActor,
            reference: this.trackable.name_label
          });
        }

        return this.sentence('PROFILE_REFERENCE.CREATE_UNNAMED', {
          candidate: candidateActor
        });

      // Feedback
      case 'feedback.create':
        return this.sentence('FEEDBACK.CREATE', {
          recruiter,
          candidate: recipientCandidate
        });

      case 'feedback.external_create': {
        const params = {
          integration: this.parameters?.external_authorization_type,
          candidate: this.object('NAME', 'NOT_ASSIGNED', this.recipient?.name_label),
          recruiter: this.object('NAME', 'NOT_ASSIGNED', this.owner?.name_label),
          date: !!this.parameters?.scheduled_date
            ? `<b>${this.parameters.scheduled_date}</b>`
            : ''
        };

        return this.sentence(
          !!params.integration
            ? 'FEEDBACK.EXTERNAL_CREATE'
            : 'FEEDBACK.EXTERNAL_CREATE_UNKNOWN_INTEGRATION',
          params
        );
      }

      case 'feedback.complete_feedback':
        return this.sentence('FEEDBACK.COMPLETE_FEEDBACK', {
          candidate: ownerName,
          recruiter: recipientRecruiter
        });

      case 'pre_screening.create':
        return this.sentence('PRE_SCREENING.CREATE', {
          recruiter,
          candidate: recipientCandidate
        });

      case 'pre_screening.complete_pre_screening':
        return this.sentence('PRE_SCREENING.COMPLETE_PRE_SCREENING', {
          candidate: ownerName
        });

      case 'background_checking.create':
        return this.sentence('BACKGROUND_CHECKING.CREATE', {
          recruiter,
          candidate: recipientCandidate
        });

      case 'background_checking.complete_background_checking':
        return this.sentence(
          'BACKGROUND_CHECKING.COMPLETE_BACKGROUND_CHECKING',
          { candidate: ownerName, recruiter: recipientRecruiter }
        );

      case 'verification_request.create':
        return this.sentence('VERIFICATION_REQUEST.CREATE', {
          recruiter,
          candidate: recipientCandidate
        });

      case 'verification_request.complete_verification_request':
        return this.sentence(
          'VERIFICATION_REQUEST.COMPLETE_VERIFICATION_REQUEST',
          { candidate: ownerName, recruiter: recipientRecruiter }
        );

      case 'recruiter_reference.view_reference':
        return this.sentence('RECRUITER_REFERENCE.VIEW_REFERENCE', {
          reference: trackableName
        });

      case 'recruiter_request.view_reference_request':
        return this.sentence('RECRUITER_REQUEST.VIEW_REFERENCE_REQUEST', {
          candidate: candidateActor
        });

      // Manual reminders
      case 'recruiter_reference.send_reminder':
      case 'profile_reference.send_reminder': {
        const params = {
          recruiter,
          reference: this.object(
            'REFERENCE',
            'A_REFERENCE',
            this.trackable?.name_label
          )
        };

        if (!!this.recipient?.name_label) {
          return this.sentence('RECRUITER_REFERENCE.SEND_REMINDER_FOR_CANDIDATE', {
            ...params,
            candidate: recipientCandidate
          });
        }

        return this.sentence('RECRUITER_REFERENCE.SEND_REMINDER', params);
      }

      case 'recruiter_request.send_reminder':
      case 'profile_request.send_reminder':
        return this.candidateReminderDescription('REFERENCE_REQUEST');

      case 'feedback.send_reminder':
        return this.candidateReminderDescription('FEEDBACK');

      case 'pre_screening.send_reminder':
        return this.candidateReminderDescription('PRE_SCREENING');

      case 'background_checking.send_reminder':
        return this.candidateReminderDescription('BACKGROUND_CHECK');

      case 'verification_request.send_reminder':
        return this.candidateReminderDescription('VERIFICATION_REQUEST');

      // Users
      case 'user.login':
        return this.sentence('USER.LOGIN', { user });

      case 'user.logout':
        return this.sentence('USER.LOGOUT', { user });

      case 'user.failed_attempt':
        return this.sentence('USER.FAILED_ATTEMPT', { user });

      case 'user.locked_out':
        return this.sentence('USER.LOCKED_OUT', { user });

      case 'user.password_updated':
        return this.sentence('USER.PASSWORD_UPDATED', { user });

      // Data exports (VIT-1359): parameters carry the export type and, for
      // CSVs, the row count.
      case 'user.export_csv':
      case 'user.export_pdf': {
        const rows = this.parameters?.rows;
        const hasRows = rows !== undefined && rows !== null;
        const hasType = !!this.parameters?.type;
        const variant = hasType
          ? hasRows
            ? 'EXPORT_OF_WITH_ROWS'
            : 'EXPORT_OF'
          : hasRows
            ? 'EXPORT_WITH_ROWS'
            : 'EXPORT';

        return this.sentence(`USER.${variant}`, {
          user,
          format: this.key === 'user.export_csv' ? 'CSV' : 'PDF',
          type: this.parameters?.type,
          rows
        });
      }

      // Comments
      case 'comment.create':
        return this.sentence('COMMENT.CREATE', { recruiter, record });

      case 'comment.update':
        return this.sentence('COMMENT.UPDATE', { recruiter, record });

      case 'comment.destroy':
        return this.sentence('COMMENT.DESTROY', { recruiter, record });

      default:
        return { key: `${FEED}.UNKNOWN` };
    }
  }

  public static fromAPIArray(res: IAPIArrayData): IActivity[] {
    const dataArray = res.data || [];
    const included = res.included as any;

    return dataArray.map((data: IAPIDataObject) => {
      if (!(data?.type === Activity.TYPE)) {
        throw new Error(
          `There was a problem parsing ${Activity.TYPE} API data`
        );
      }

      const activity = new Activity({
        id: data.id,
        ...data.attributes
      });

      const relationships = data?.relationships as any;
      const ownerRelationship = relationships.owner as IAPIRelationship;
      const trackableRelationship = relationships.trackable as IAPIRelationship;
      const recipientRelationship = relationships.recipient as IAPIRelationship;

      const ownerData = included.find(
        (i: IAPIDataObject) => i.id === ownerRelationship.id
      );

      const trackableData = included.find(
        (i: IAPIDataObject) => i.id === trackableRelationship.id
      );

      activity.owner = this.initRelatedData(ownerData, included);
      activity.trackable = this.initRelatedData(trackableData, included);

      if (recipientRelationship) {
        const recipientData = included.find(
          (i: IAPIDataObject) =>
            i.type === recipientRelationship.type &&
            i.id === recipientRelationship.id
        );

        activity.recipient = this.initRelatedData(recipientData, included);
      }

      return activity;
    });
  }

  public static initRelatedData(data: any, included: any[] = []): any {
    if (!data) {
      return null;
    }

    switch (data.type) {
      case 'Recruiter':
        return Recruiter.fromAPI({
          data,
          included
        });

      case 'Candidate':
        return new Candidate({
          id: data.id,
          ...data.attributes
        });

      case 'RecruiterReference':
      case 'Reference':
        return new Reference({
          id: data.id,
          ...data.attributes
        });

      case 'QuestionSet':
        return new QuestionSet({
          id: data.id,
          ...data.attributes
        });

      case 'User':
        return new User({
          id: data.id,
          ...data.attributes
        });

      case 'Comment':
        return {
          id: data.id,
          ...data.attributes
        };

      default:
        return null;
    }
  }

  // Shared wording for candidate document activities. `parameters.source` says
  // who acted: the candidate, through the public reference-request form, or a
  // recruiter — also the reading for rows recorded before the source was stored.
  private candidateDocumentDescription(
    action: 'UPLOAD_DOCUMENT' | 'DELETE_DOCUMENT'
  ): ITranslatable {
    const document = this.object(
      'DOCUMENT',
      'A_DOCUMENT',
      this.parameters?.filename,
      'filename'
    );

    if (this.parameters?.source === 'candidate') {
      return this.sentence(`CANDIDATE.${action}_BY_CANDIDATE`, {
        candidate: this.actor('CANDIDATE', this.owner?.name_label),
        document
      });
    }

    return this.sentence(`CANDIDATE.${action}`, {
      recruiter: this.actor('RECRUITER', this.owner?.name_label),
      document,
      candidate: this.object(
        'CANDIDATE_LOWERCASE',
        'A_CANDIDATE',
        this.trackable?.name_label
      )
    });
  }

  // Shared wording for '<request type>.send_reminder' activities, where the
  // trackable is the candidate the manual reminder was sent to.
  private candidateReminderDescription(requestType: string): ITranslatable {
    return this.sentence('REQUEST_REMINDER', {
      recruiter: this.actor('RECRUITER', this.owner?.name_label),
      type: { key: `${REQUEST_TYPES}.${requestType}` },
      candidate: this.object(
        'CANDIDATE',
        'A_CANDIDATE',
        this.trackable?.name_label
      )
    });
  }

  // Resolves the candidate status inside a serialized `parameters.old` /
  // `parameters.new` payload to its STATUSES.CANDIDATES.* key; the stored
  // value can be an integer (old) or the raw request param (new).
  private statusKey(values: any): string | null {
    if (!values) {
      return null;
    }

    try {
      const parsed = typeof values === 'string' ? JSON.parse(values) : values;
      const status = parsed?.status;

      if (status === null || status === undefined || status === '') {
        return null;
      }

      return (
        candidatesStatus.find((s) => s.id === Number(status))?.key ?? null
      );
    } catch {
      return null;
    }
  }

  private getList(key: string, obj: any): ITranslatable[] {
    const parsed = typeof obj === 'string' ? JSON.parse(obj) : obj;

    return Object.entries(parsed).map(([field, value]) => ({
      key,
      params: { field, value: String(value) }
    }));
  }

  private sentence(
    key: string,
    params?: { [name: string]: TranslatableParam }
  ): ITranslatable {
    return { key: `${SENTENCES}.${key}`, params };
  }

  // Sentence-initial fragment: "Recruiter <b>X</b>" / "A recruiter",
  // "Candidate <b>X</b>" / "A candidate", "<b>X</b>" / "Someone".
  private actor(kind: ActorKind, name?: string): ITranslatable {
    return this.fragment(ACTORS, kind, kind === 'NAME' ? 'SOMEONE' : `A_${kind}`, name);
  }

  // Mid-sentence fragment: the `known` key with the name when there is one,
  // the `unknown` key otherwise.
  private object(
    known: string,
    unknown: string,
    name?: string,
    param: string = 'name'
  ): ITranslatable {
    return this.fragment(OBJECTS, known, unknown, name, param);
  }

  private fragment(
    scope: string,
    known: string,
    unknown: string,
    name?: string,
    param: string = 'name'
  ): ITranslatable {
    return !!name
      ? { key: `${scope}.${known}`, params: { [param]: name } }
      : { key: `${scope}.${unknown}` };
  }
}
